/* ============================================================
   JFC Motion Layer — reveal-on-scroll + parallax (vanilla, no deps)
   - Reveals: IntersectionObserver, fires once, optional stagger.
   - Parallax: rAF-throttled, desktop + pointer only, skipped for
     prefers-reduced-motion.
   ============================================================ */
(function () {
  'use strict';

  var docEl = document.documentElement;
  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Mark the document so the reveal CSS engages (progressive enhancement).
  docEl.classList.add('jfc-motion');

  /* ── Expand staggered groups into individual reveal targets ──────── */
  var groups = [].slice.call(document.querySelectorAll('[data-reveal-stagger]'));
  groups.forEach(function (group) {
    var step = parseInt(group.getAttribute('data-reveal-stagger') || '80', 10);
    var base = parseInt(group.getAttribute('data-reveal-delay') || '0', 10);
    [].slice.call(group.children).forEach(function (child, i) {
      if (!child.hasAttribute('data-reveal')) child.setAttribute('data-reveal', '');
      child.setAttribute('data-reveal-delay', base + i * step);
    });
  });

  /* ── Reveal on scroll ────────────────────────────────────────────── */
  var revealEls = [].slice.call(document.querySelectorAll('[data-reveal]'));

  if (revealEls.length) {
    if (reduce || !('IntersectionObserver' in window)) {
      revealEls.forEach(function (el) { el.classList.add('is-revealed'); });
    } else {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var el = entry.target;
          var delay = parseInt(el.getAttribute('data-reveal-delay') || '0', 10);
          if (delay) el.style.transitionDelay = delay + 'ms';
          el.classList.add('is-revealed');
          // Drop the delay once shown so re-layouts don't re-stagger.
          if (delay) setTimeout(function () { el.style.transitionDelay = ''; }, delay + 750);
          io.unobserve(el);
        });
      }, { threshold: 0.05, rootMargin: '0px 0px 14% 0px' });

      revealEls.forEach(function (el) { io.observe(el); });

      // Anything already on-screen at load reveals immediately (no wait).
      requestAnimationFrame(function () {
        revealEls.forEach(function (el) {
          var r = el.getBoundingClientRect();
          if (r.top < window.innerHeight && r.bottom > 0 && r.top >= 0) {
            el.classList.add('is-revealed');
            io.unobserve(el);
          }
        });
      });
    }
  }

  /* ── Parallax ────────────────────────────────────────────────────── */
  var canParallax = !reduce &&
    window.matchMedia &&
    window.matchMedia('(min-width: 750px)').matches &&
    window.matchMedia('(pointer: fine)').matches;

  var pxEls = [].slice.call(document.querySelectorAll('[data-parallax]'));

  if (canParallax && pxEls.length) {
    var ticking = false;

    function update() {
      var vh = window.innerHeight;
      pxEls.forEach(function (el) {
        var r = el.getBoundingClientRect();
        if (r.bottom < -240 || r.top > vh + 240) return; // off-screen: skip
        var speed = parseFloat(el.getAttribute('data-parallax') || '0.1');
        var center = r.top + r.height / 2;
        var delta = center - vh / 2;          // distance from viewport centre
        var shift = (delta * -speed).toFixed(2);
        el.style.transform = 'translate3d(0,' + shift + 'px,0)';
      });
      ticking = false;
    }

    function onScroll() {
      if (!ticking) { ticking = true; requestAnimationFrame(update); }
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();
  }

  /* ── Count-up numbers ────────────────────────────────────────────────
     <span data-countup>48h</span> animates 0 → 48 (keeps any suffix) the
     first time it scrolls into view. easeOutExpo for a confident settle. */
  var countEls = [].slice.call(document.querySelectorAll('[data-countup]'));
  if (countEls.length) {
    function runCount(el) {
      var raw = (el.getAttribute('data-countup-value') || el.textContent).trim();
      var m = raw.match(/^([\d.,]+)(.*)$/);
      if (!m) return;
      var target = parseFloat(m[1].replace(/,/g, ''));
      var suffix = m[2] || '';
      var decimals = (m[1].split('.')[1] || '').length;
      if (reduce) { el.textContent = raw; return; }
      var dur = 1300, start = null;
      function frame(ts) {
        if (start === null) start = ts;
        var p = Math.min((ts - start) / dur, 1);
        var eased = p === 1 ? 1 : 1 - Math.pow(2, -10 * p); // easeOutExpo
        var val = target * eased;
        el.textContent = (decimals ? val.toFixed(decimals) : Math.round(val)) + suffix;
        if (p < 1) requestAnimationFrame(frame);
      }
      requestAnimationFrame(frame);
    }
    if (!('IntersectionObserver' in window)) {
      countEls.forEach(function (el) { el.setAttribute('data-countup-value', el.textContent.trim()); runCount(el); });
    } else {
      var cio = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (!e.isIntersecting) return;
          runCount(e.target); cio.unobserve(e.target);
        });
      }, { threshold: 0.6 });
      countEls.forEach(function (el) { el.setAttribute('data-countup-value', el.textContent.trim()); cio.observe(el); });
    }
  }

  /* ── Magnetic CTAs ───────────────────────────────────────────────────
     [data-magnetic] gently pulls toward the cursor (desktop, fine pointer)
     and springs back on leave. Subtle — max ~9px — so it reads premium. */
  if (!reduce && window.matchMedia && window.matchMedia('(pointer: fine)').matches) {
    [].slice.call(document.querySelectorAll('[data-magnetic]')).forEach(function (el) {
      var strength = parseFloat(el.getAttribute('data-magnetic') || '0.3');
      var max = 9;
      el.addEventListener('mousemove', function (e) {
        var r = el.getBoundingClientRect();
        var x = (e.clientX - (r.left + r.width / 2)) * strength;
        var y = (e.clientY - (r.top + r.height / 2)) * strength;
        x = Math.max(-max, Math.min(max, x));
        y = Math.max(-max, Math.min(max, y));
        el.style.transition = 'transform 0s';
        el.style.transform = 'translate(' + x.toFixed(1) + 'px,' + y.toFixed(1) + 'px)';
      });
      el.addEventListener('mouseleave', function () {
        el.style.transition = 'transform 0.45s cubic-bezier(0.16,1,0.3,1)';
        el.style.transform = 'translate(0,0)';
      });
    });
  }

  /* ── Scroll-progress thread ──────────────────────────────────────────
     A 2px brand-tinted line at the very top that tracks read progress. */
  if (!reduce) {
    var bar = document.createElement('div');
    bar.className = 'jfc-scroll-progress';
    document.body.appendChild(bar);
    var pTick = false;
    function paintBar() {
      var h = document.documentElement.scrollHeight - window.innerHeight;
      var p = h > 0 ? (window.scrollY / h) : 0;
      bar.style.transform = 'scaleX(' + Math.min(1, Math.max(0, p)).toFixed(4) + ')';
      pTick = false;
    }
    window.addEventListener('scroll', function () {
      if (!pTick) { pTick = true; requestAnimationFrame(paintBar); }
    }, { passive: true });
    paintBar();
  }
})();
